import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AlertController } from '@ionic/angular/standalone';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { ApiResponse, RoleAssignment } from '@app/core/models/user.model';
import { Constants } from '@app/core/services/constants';
import { ContextService } from '@app/core/services/context.service';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';

interface RoleOption {
  roleId: string;
  isSystem?: boolean;
  jsonPayload?: {
    name?: string;
    names?: Record<string, string>;
    helpText?: string;
    helpTexts?: Record<string, string>;
    [key: string]: unknown;
  };
}

interface SubsidiaryOption {
  subsidiaryId: string;
  name: string;
}

interface RoleListResponse extends ApiResponse<RoleOption[]> {}
interface SubsidiaryListResponse extends ApiResponse<SubsidiaryOption[]> {}

interface PaginationInfo {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

interface PagedApiResponse<T> extends ApiResponse<T[]> {
  pagination?: PaginationInfo;
}

@Component({
  selector: 'app-formly-field-user-role-assignments',
  templateUrl: './formly-field-user-role-assignments.component.html',
  styleUrls: ['./formly-field-user-role-assignments.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
  ],
})
export class FormlyFieldUserRoleAssignmentsComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  public roleOptions: RoleOption[] = [];
  public subsidiaryOptions: SubsidiaryOption[] = [];
  public roleAssignments: RoleAssignment[] = [];
  public isLoading = true;
  public loadError = false;

  private readonly http = inject(HttpClient);
  private readonly contextService = inject(ContextService);
  private readonly translateService = inject(TranslateService);
  private readonly alertController = inject(AlertController);
  private ignoreNextValueSync = false;
  private valueSubscription?: Subscription;

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.valueSubscription = this.formControl.valueChanges.subscribe((value) => {
      if (this.ignoreNextValueSync) {
        this.ignoreNextValueSync = false;
        this.changeDetectorRef.markForCheck();
        return;
      }

      this.syncFromValue(value);
      this.changeDetectorRef.markForCheck();
    });

    this.syncFromValue(this.formControl.value);
    await this.loadOptions();
  }

  ngOnDestroy(): void {
    this.valueSubscription?.unsubscribe();
  }

  public get isReadonly(): boolean {
    return Boolean(this.options.formState?.disabled || this.field.props?.disabled || this.field.props?.readonly);
  }

  public get clientId(): string {
    return this.model?.clientId || this.contextService.getCurrentClientId() || '';
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public trackByRoleId(_index: number, role: RoleOption): string {
    return role.roleId;
  }

  public getRoleOptionLabel(role: RoleOption): string {
    return this.resolveRoleLabel(role);
  }

  public hasRoleHelpText(role: RoleOption): boolean {
    return this.resolveRoleHelpText(role).length > 0;
  }

  public getRoleLabel(roleId: string | undefined): string {
    if (!roleId) {
      return '-';
    }

    const role = this.roleOptions.find((item) => item.roleId === roleId);
    return role ? this.resolveRoleLabel(role) : roleId;
  }

  public getSubsidiaryLabel(subsidiaryId: string | undefined): string {
    if (!subsidiaryId) {
      return this.translateService.instant('USER_ROLE_ASSIGNMENTS.ALL_SUBSIDIARIES');
    }

    const subsidiary = this.subsidiaryOptions.find((item) => item.subsidiaryId === subsidiaryId);
    return subsidiary ? `${subsidiary.name} (${subsidiary.subsidiaryId})` : subsidiaryId;
  }

  public isRoleAssigned(roleId: string): boolean {
    return this.roleAssignments.some((assignment) => assignment.roleId === roleId);
  }

  public getSubsidiaryValueForRole(roleId: string): string {
    return this.roleAssignments.find((assignment) => assignment.roleId === roleId)?.subsidiaryId || '';
  }

  public onRoleCheckedChange(roleId: string, checked: boolean): void {
    if (this.isReadonly) {
      return;
    }

    if (checked) {
      if (!this.isRoleAssigned(roleId)) {
        this.roleAssignments = [
          ...this.roleAssignments,
          { roleId, subsidiaryId: undefined },
        ];
      }
    } else {
      this.roleAssignments = this.roleAssignments.filter((assignment) => assignment.roleId !== roleId);
    }

    this.writeValue();
  }

  public onSubsidiaryChange(roleId: string, value: string): void {
    if (this.isReadonly) {
      return;
    }

    const normalizedValue = value.trim() ? value.trim() : undefined;
    this.roleAssignments = this.roleAssignments.map((assignment) => (
      assignment.roleId === roleId
        ? { ...assignment, subsidiaryId: normalizedValue }
        : assignment
    ));
    this.writeValue();
  }

  public async retryLoad(): Promise<void> {
    await this.loadOptions(true);
  }

  public async showRoleHelp(role: RoleOption, event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const helpText = this.resolveRoleHelpText(role);
    if (!helpText) {
      return;
    }

    const alert = await this.alertController.create({
      // header: this.resolveRoleLabel(role),
      message: helpText,
      buttons: [this.translateService.instant('COMMON.CLOSE') || 'Close'],
    });

    await alert.present();
  }

  private async loadOptions(forceReload = false): Promise<void> {
    this.isLoading = true;
    this.loadError = false;
    this.changeDetectorRef.markForCheck();

    try {
      const [roles, subsidiaries] = await Promise.all([
        this.fetchAllPages<RoleOption>(`${environment.apiUrl}/roles`, 'pageSize', {
          isActive: 'true',
        }, forceReload),
        this.clientId
          ? this.fetchAllPages<SubsidiaryOption>(`${environment.apiUrl}/subsidiaries/${this.clientId}`, 'limit', {
              isActive: 'true',
            }, forceReload)
          : Promise.resolve([] as SubsidiaryOption[]),
      ]);

      this.roleOptions = roles;
      this.subsidiaryOptions = subsidiaries;
      this.loadError = false;
    } catch (error) {
      console.error('Failed to load user role assignment options', error);
      this.roleOptions = [];
      this.subsidiaryOptions = [];
      this.loadError = true;
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  private async fetchAllPages<T>(
    url: string,
    pageSizeParam: 'limit' | 'pageSize',
    baseParams: Record<string, string>,
    forceReload: boolean,
  ): Promise<T[]> {
    const collectedItems: T[] = [];
    let currentPage = 1;
    let totalPages = 1;

    do {
      const response = await firstValueFrom(this.http.get<PagedApiResponse<T>>(url, {
        params: {
          ...baseParams,
          page: currentPage.toString(),
          [pageSizeParam]: Constants.maxPageSize.toString(),
          ...(forceReload ? { _: `${Date.now()}-${currentPage}` } : {}),
        },
      }));

      if (!response?.success) {
        throw new Error(response?.message || `Failed to fetch paged data from ${url}`);
      }

      collectedItems.push(...(Array.isArray(response.data) ? response.data : []));
      totalPages = Math.max(response.pagination?.totalPages || 1, 1);
      currentPage += 1;
    } while (currentPage <= totalPages);

    return collectedItems;
  }

  private syncFromValue(value: unknown): void {
    const normalizedAssignments = Array.isArray(value)
      ? value
          .filter((entry): entry is RoleAssignment => Boolean(entry) && typeof entry === 'object')
          .map((entry) => ({
            roleId: typeof entry.roleId === 'string' ? entry.roleId : '',
            subsidiaryId: typeof entry.subsidiaryId === 'string' && entry.subsidiaryId.trim().length > 0 ? entry.subsidiaryId : undefined,
          }))
      : [];

    const assignmentsByRoleId = new Map<string, RoleAssignment>();
    for (const assignment of normalizedAssignments) {
      if (!assignment.roleId) {
        continue;
      }
      assignmentsByRoleId.set(assignment.roleId, assignment);
    }

    this.roleAssignments = Array.from(assignmentsByRoleId.values());
  }

  private writeValue(): void {
    const nextValue = this.roleAssignments
      .map((assignment) => ({
        roleId: assignment.roleId.trim(),
        subsidiaryId: assignment.subsidiaryId?.trim() || undefined,
      }))
      .filter((assignment) => assignment.roleId.length > 0);

    this.ignoreNextValueSync = true;
    this.formControl.setValue(nextValue);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.onChange(nextValue);
    this.changeDetectorRef.markForCheck();
  }

  private resolveRoleLabel(role: RoleOption): string {
    const names = role.jsonPayload?.names;
    const currentLocale = this.translateService.currentLang || this.translateService.getDefaultLang() || 'fr-FR';
    const languageOnlyLocale = currentLocale.split('-')[0];

    if (names && typeof names === 'object') {
      const localizedName = names[currentLocale]
        || names[languageOnlyLocale]
        || names['fr-FR']
        || names['en-US']
        || Object.values(names).find((value) => typeof value === 'string' && value.trim().length > 0);

      if (typeof localizedName === 'string' && localizedName.trim().length > 0) {
        return localizedName;
      }
    }

    if (typeof role.jsonPayload?.name === 'string' && role.jsonPayload.name.trim().length > 0) {
      return role.jsonPayload.name;
    }

    return role.roleId;
  }

  private resolveRoleHelpText(role: RoleOption): string {
    const helpTexts = role.jsonPayload?.helpTexts;
    const currentLocale = this.translateService.currentLang || this.translateService.getDefaultLang() || 'fr-FR';
    const languageOnlyLocale = currentLocale.split('-')[0];

    if (helpTexts && typeof helpTexts === 'object') {
      const localizedHelpText = helpTexts[currentLocale]
        || helpTexts[languageOnlyLocale]
        || helpTexts['fr-FR']
        || helpTexts['en-US']
        || Object.values(helpTexts).find((value) => typeof value === 'string' && value.trim().length > 0);

      if (typeof localizedHelpText === 'string' && localizedHelpText.trim().length > 0) {
        return localizedHelpText;
      }
    }

    if (typeof role.jsonPayload?.helpText === 'string' && role.jsonPayload.helpText.trim().length > 0) {
      return role.jsonPayload.helpText;
    }

    return '';
  }
}